import { GQLProductSideAvailableDecorationType } from '@theprintbar/graphql';
import { z } from 'zod';
import { zfd } from 'zod-form-data';
import { getCurrencyString, getCurrencyValue } from '../utils';

export const rgbArray = z
	.string()
	.transform(str => str.split(',').map(Number))
	.refine(numArray => numArray.every(num => Number.isInteger(num) && num >= 0 && num <= 255), {
		message: 'All elements must be integers between 0 and 255',
	});

export const printProcessField = z.string().refine(value => {
	return Object.values(GQLProductSideAvailableDecorationType).includes(value as any);
}, 'Must be a valid process type');

export const cleanTextField = zfd
	.text()
	.refine(value => !value || value.length <= 255, {
		message: 'Must be less than 255 characters',
	})
	.refine(value => !value || /^(?:(?!http|www\.)[\s\S])*$/i.test(value), {
		message: 'Links are not permitted',
	});

export interface DollarProps {
	min?: number;
	max?: number;
}
export const dollarField = (props?: DollarProps) =>
	z.preprocess(
		value => {
			return getCurrencyValue(value as any);
		},
		z.coerce
			.number({ invalid_type_error: 'Please enter a valid number' })
			.gte(props?.min || 0, `Must be ${getCurrencyString(props?.min)} or greater`)
			.lte(props?.max || Infinity, `Must be ${getCurrencyString(props?.max)} or less`),
	);

export const addressSchema = z.object({
	streetAddress: z.string().min(1, 'Please enter your address').max(100, 'Max length of 100'),
	streetAddress2: z.string().max(100, 'Max length of 100').optional().nullable(),
	city: z.string().min(1, 'Please enter your city').max(100, 'Max length of 100'),
	state: z.string().min(1, 'Please enter your state').max(100, 'Max length of 100'),
	postCode: z.string().min(1, 'Please enter your postal code').max(20, 'Max length of 20'),
	country: z.string().min(1, 'Please enter your country').max(100, 'Max length of 100'),
});

export const contactSchema = z.object({
	firstName: z.string().min(1, 'Please enter your first name').max(100, 'Max length of 100'),
	lastName: z.string().min(1, 'Please enter your last name').max(100, 'Max length of 100'),
	company: z.string().max(100, 'Max length of 100').optional().nullable(),
	email: z.string().email('Please enter a valid email').max(254, 'Max length of 254'),
	phone: z.string().max(20, 'Max length of 20').optional().nullable(),
});

export const addressWithContactSchema = addressSchema.extend({
	...contactSchema.shape,
});

export type AddressType = z.infer<typeof addressSchema>;
export type AddressWithContactType = z.infer<typeof addressWithContactSchema>;

export const serverFile = z.object({
	upload: z.any(),
});

//Requred checkbox field
export const zodRequiredCheckbox = zfd.checkbox().refine(val => val, 'This field is required');

interface ClientFileProps {
	optional?: boolean;
	maxSize?: number;
	fileTypes?: string[];
}
export const clientFile = (config?: ClientFileProps) =>
	z.preprocess(
		val => {
			//Empty File object on no user input, so convert to undefined
			return (val instanceof File && val.size === 0) || !val ? undefined : val;
		},
		z.custom<File>().superRefine((file, ctx) => {
			if (!file && !config?.optional) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'File is required',
				});
				return false;
			}

			if (config?.fileTypes && !config.fileTypes.includes(file.type)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'File must be a valid image type',
				});
				return false;
			}

			if (config?.maxSize && file.size > 1024 * 1024 * 5) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'File must be less than 5MB',
				});
				return false;
			}

			return true;
		}),
	);

export const booleanField = () =>
	z
		.preprocess(
			val =>
				val === null || val === undefined
					? undefined
					: typeof val === 'boolean'
					? val
					: typeof val === 'string'
					? ['on', 'true'].includes(val)
					: false,
			z.boolean(),
		)
		.optional();
